/**
 * Sprint B — Sélecteur de postes DPGF en accordéon.
 *
 * Affiche les lots du chantier (depuis /dpgf/lignes-disponibles), chaque lot
 * dépliable, avec ses postes cochables. Récap des montants, badge attribution,
 * recherche par désignation. Sauvegarde via POST /elements/{eid}/lignes-dpgf.
 *
 * Props :
 *  - chantierId
 *  - elementId
 *  - lignesActuelles : [{ lot_id, poste_id }]
 *  - onSaved(updatedElement) : callback succès
 */
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CaretDown, CaretRight, MagnifyingGlass, Check, X, CheckCircle } from '@phosphor-icons/react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtMontant = (v) => (v == null ? '—' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v));

export default function DpgfPosteSelector({ chantierId, elementId, lignesActuelles = [], onSaved }) {
  const [data, setData] = useState({ lots: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openLots, setOpenLots] = useState({});
  const [selected, setSelected] = useState(() => new Set(lignesActuelles.map((l) => `${l.lot_id}::${l.poste_id}`)));
  const [saving, setSaving] = useState(false);

  const initialKeys = useMemo(
    () => new Set(lignesActuelles.map((l) => `${l.lot_id}::${l.poste_id}`)),
    [lignesActuelles],
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API_URL}/api/chantiers/${chantierId}/dpgf/lignes-disponibles`, { withCredentials: true })
      .then((r) => {
        if (!alive) return;
        setData(r.data || { lots: [] });
        // Auto-ouvre les lots avec une sélection courante
        const opens = {};
        const lots = r.data?.lots || [];
        lots.forEach((l) => {
          if (lignesActuelles.some((x) => x.lot_id === l.lot_id)) {
            opens[l.lot_id] = true;
          }
        });
        setOpenLots(opens);
      })
      .catch(() => toast.error('Impossible de charger le DPGF'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [chantierId, lignesActuelles]);

  const filteredLots = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.lots || [];
    return (data.lots || [])
      .map((l) => {
        const matchLot = (l.lot_nom || '').toLowerCase().includes(q);
        const postes = (l.postes || []).filter((p) => (p.designation || '').toLowerCase().includes(q));
        if (matchLot) return l;
        if (postes.length) return { ...l, postes };
        return null;
      })
      .filter(Boolean);
  }, [data.lots, search]);

  const toggle = (lot_id, poste_id) => {
    const key = `${lot_id}::${poste_id}`;
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelected(next);
  };

  const toggleLotOpen = (lot_id) => setOpenLots((o) => ({ ...o, [lot_id]: !o[lot_id] }));

  const totalSelected = selected.size;
  const dirty = selected.size !== initialKeys.size
    || [...selected].some((k) => !initialKeys.has(k))
    || [...initialKeys].some((k) => !selected.has(k));

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const toAdd = [...selected]
        .filter((k) => !initialKeys.has(k))
        .map((k) => { const [lot_id, poste_id] = k.split('::'); return { lot_id, poste_id }; });
      const toRemove = [...initialKeys]
        .filter((k) => !selected.has(k))
        .map((k) => k.split('::'));

      // Détacher (séquentiel : endpoint single)
      for (const [lot_id, poste_id] of toRemove) {
        await axios.delete(
          `${API_URL}/api/chantiers/${chantierId}/elements/${elementId}/lignes-dpgf`,
          { withCredentials: true, params: { lot_id, poste_id } },
        );
      }
      let updated = null;
      if (toAdd.length) {
        const { data: u } = await axios.post(
          `${API_URL}/api/chantiers/${chantierId}/elements/${elementId}/lignes-dpgf`,
          { lignes: toAdd },
          { withCredentials: true },
        );
        updated = u;
      }
      // Si on n'a fait que retirer, fetch final
      if (!updated && toRemove.length) {
        const { data: u } = await axios.get(
          `${API_URL}/api/chantiers/${chantierId}/elements/${elementId}`,
          { withCredentials: true },
        );
        updated = u;
      }
      toast.success('Rattachements DPGF mis à jour');
      onSaved?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4 text-sm text-[#9A9A9A]" data-testid="dpgf-selector-loading">Chargement DPGF…</div>;

  return (
    <div className="space-y-3" data-testid="dpgf-poste-selector">
      <div className="flex items-center gap-2 px-1">
        <MagnifyingGlass size={14} className="text-[#9A9A9A]" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un poste…"
          className="flex-1 px-2 py-1 border border-[#E0E0DB] rounded-none text-sm focus:outline-none focus:border-[#C4773B]"
          data-testid="dpgf-selector-search"
        />
        {totalSelected > 0 && (
          <span className="text-[10px] uppercase tracking-wider font-bold text-[#1E3A5F]" data-testid="dpgf-selector-count">
            {totalSelected} {totalSelected === 1 ? 'ligne' : 'lignes'}
          </span>
        )}
      </div>

      <div className="border border-[#E0E0DB] divide-y divide-[#E0E0DB] max-h-80 overflow-y-auto">
        {(filteredLots || []).length === 0 ? (
          <div className="p-4 text-center text-xs text-[#9A9A9A] italic" data-testid="dpgf-selector-empty">
            Aucun poste DPGF{search ? ' ne correspond' : ' disponible'}.
          </div>
        ) : (
          filteredLots.map((lot) => {
            const open = !!openLots[lot.lot_id];
            const selectedInLot = (lot.postes || []).filter((p) => selected.has(`${lot.lot_id}::${p.poste_id}`)).length;
            return (
              <div key={lot.lot_id} data-testid={`dpgf-selector-lot-${lot.lot_id}`}>
                <button
                  type="button"
                  onClick={() => toggleLotOpen(lot.lot_id)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#FAFAF8] text-left"
                  data-testid={`dpgf-selector-lot-toggle-${lot.lot_id}`}
                >
                  {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-[#2C2C2C] truncate">{lot.lot_nom || `Lot ${lot.lot_id || '—'}`}</span>
                      {lot.lot_attribue && (
                        <span
                          className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-none flex items-center gap-1"
                          style={{ background: '#E8F3EE', color: '#2D6A4F' }}
                        >
                          <CheckCircle size={10} weight="bold" /> Attribué
                        </span>
                      )}
                      {selectedInLot > 0 && (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-none bg-[#FDF1E1] text-[#C4773B]">
                          {selectedInLot} sél.
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-[#9A9A9A] mt-0.5">
                      {lot.entreprise_attribuee ? `${lot.entreprise_attribuee} · ` : ''}
                      {lot.postes?.length || 0} postes · {fmtMontant(lot.montant_marche)}
                    </div>
                  </div>
                </button>
                {open && (
                  <div className="bg-[#FAFAF8] divide-y divide-[#EFEFEC]">
                    {(lot.postes || []).map((p) => {
                      const key = `${lot.lot_id}::${p.poste_id}`;
                      const checked = selected.has(key);
                      return (
                        <label
                          key={p.poste_id}
                          className="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-white"
                          data-testid={`dpgf-selector-poste-${p.poste_id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(lot.lot_id, p.poste_id)}
                            className="mt-1"
                            data-testid={`dpgf-selector-poste-check-${p.poste_id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-[#2C2C2C]">{p.designation}</div>
                            <div className="text-[10px] text-[#9A9A9A] mt-0.5">
                              {p.quantite} {p.unite} × {fmtMontant(p.prix_unitaire)} = <span className="font-semibold text-[#1E3A5F]">{fmtMontant(p.montant_total)}</span>
                              {p.nb_taches > 0 && (
                                <span className="ml-2 text-[#C4773B] font-bold">· {p.nb_taches} tâche{p.nb_taches > 1 ? 's' : ''}</span>
                              )}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button
          onClick={handleSave}
          disabled={!dirty || saving}
          className="rounded-none bg-[#1E3A5F] hover:bg-[#152a48] text-white"
          data-testid="dpgf-selector-save"
        >
          <Check size={14} className="mr-1" />
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
}
